// lab/src/component/HomePage/CategorySection/AllPackages.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { 
  Search, Filter, Heart, Star, Clock, ArrowRight, 
  ShoppingBag, Package as PackageIcon, Droplet, 
  Activity, Wind, Shield, TrendingUp,
  ChevronDown, ChevronUp, X, Loader2,
  GitCompare, Check, Minus, Plus,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import GetPackageDetails from '../../LabTest/GetPackageDetails';
import ReplaceCartPopup from '../../LabTest/ReplaceCartPopup';
import { usePackageCartActions } from '../../../hooks/usePackageCartActions';
import { useCartActions } from '../../../hooks/useCartActions';
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from '../../../Redux/wishlistSlice';
import { fetchAllRatings } from '../../../Redux/ratingSlice';
import LoginPopup from '../../common/LoginPopup';
import Coupon from '../../Coupon/Coupon';

// ✅ CORRECT IMPORT PATH
import {
  fetchAllPackagesFromAPI,
  getUserId,
  calculatePackagePrice,
  getTestsCount,
  getUniqueLabs,
  getUniqueCompanies,
  filterAndSortPackages
} from '../../../services/HomePage/AllPackagesAPI';

export default function AllPackages() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const cart = useSelector((state) => state.cart.items || []);
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLab, setSelectedLab] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 20000 });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE = 4;
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [showPackageDetails, setShowPackageDetails] = useState(false);

  // Local state for login popup
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const { addPackage, removePackage, hasDifferentStore } = usePackageCartActions();
  
  const { 
    addWithReplaceCheck, 
    removeItem, 
    LoginPopupComponent
  } = useCartActions();

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const isInWishlist = (productId, categoryId = 3) => {
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const toggleWishlist = async (pkg, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = pkg.packageId;
    const categoryId = 3;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  const getPackageIcon = (name) => {
    if (!name) return <PackageIcon size={24} />;
    const nameLower = name.toLowerCase();
    if (nameLower.includes('diabetes')) return <Droplet size={24} />;
    if (nameLower.includes('heart') || nameLower.includes('cardiac')) return <Activity size={24} />;
    if (nameLower.includes('thyroid')) return <Wind size={24} />;
    if (nameLower.includes('lft') || nameLower.includes('liver')) return <PackageIcon size={24} />;
    if (nameLower.includes('full') || nameLower.includes('body')) return <Shield size={24} />;
    return <PackageIcon size={24} />;
  };

  const handlePackageClick = (pkg) => {
    setSelectedPackageId(pkg.packageId);
    setShowPackageDetails(true);
  };

  const isPackageAdded = (packageId) => {
    return cart.some(
      (item) =>
        Number(item.productId) === Number(packageId) &&
        item.categoryId === 3
    );
  };

  const handlePackageAction = (pkg, e) => {
    e.stopPropagation();
    
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }
    
    const payload = {
      productId: pkg.packageId,
      categoryId: 3,
      price: Number(pkg.packagePrice) || 0,
      storeId: Number(pkg.labId) || 0,
      productName: pkg.packageName || "",
    };
    
    const added = isPackageAdded(pkg.packageId);
    if (added) {
      removeItem(payload);
    } else {
      addWithReplaceCheck(payload);
    }
  };

  const addToCompare = (pkg) => {
    if (compareList.some(item => item.packageId === pkg.packageId)) {
      setCompareList(compareList.filter(item => item.packageId !== pkg.packageId));
      return;
    }
    
    if (compareList.length >= MAX_COMPARE) {
      alert(`You can compare maximum ${MAX_COMPARE} packages at a time.`);
      return;
    }
    
    setCompareList([...compareList, pkg]);
  };

  const removeFromCompare = (packageId) => {
    setCompareList(compareList.filter(item => item.packageId !== packageId));
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  const isInCompare = (packageId) => {
    return compareList.some(item => item.packageId === packageId);
  };

  const getPackageRating = (packageId) => {
    const key = `${packageId}_3`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const fetchAllPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const activePackages = await fetchAllPackagesFromAPI();
      
      setPackages(activePackages);
      setFilteredPackages(activePackages);
      setCurrentPage(1);
      
      if (activePackages.length > 0) {
        const ratingProducts = activePackages.map(pkg => ({
          productId: pkg.packageId,
          categoryId: 3
        }));
        await dispatch(fetchAllRatings(ratingProducts));
      }
      
    } catch (err) {
      console.error('API Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPackages();
    loadWishlist();
  }, []);

  useEffect(() => {
    const filters = { searchTerm, selectedLab, selectedCompany, sortBy, priceRange };
    const filtered = filterAndSortPackages(packages, filters);
    setFilteredPackages(filtered);
    setCurrentPage(1);
  }, [packages, searchTerm, selectedLab, selectedCompany, sortBy, priceRange]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPackages = filteredPackages.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const uniqueLabs = getUniqueLabs(packages);
  const uniqueCompanies = getUniqueCompanies(packages);

  const renderStars = (rating, size = 12) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={size}
          className={`${
            i <= roundedRating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  const CompareModal = () => {
    if (!showCompareModal || compareList.length < 2) return null;

    const getAllTestNames = () => {
      const testSet = new Set();
      compareList.forEach(pkg => {
        if (pkg.testPackageDetailsJson) {
          try {
            const tests = JSON.parse(pkg.testPackageDetailsJson);
            if (Array.isArray(tests)) {
              tests.forEach(test => {
                if (test.testName) testSet.add(test.testName);
              });
            }
          } catch {}
        }
      });
      return Array.from(testSet);
    };

    const allTestNames = getAllTestNames();

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-gradient-to-r from-red-50 to-red-100 p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitCompare size={24} className="text-red-600" />
              <h2 className="text-xl font-bold text-gray-800">
                Compare Packages ({compareList.length}/{MAX_COMPARE})
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearCompare}
                className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowCompareModal(false)}
                className="p-2 hover:bg-red-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="overflow-auto p-4 max-h-[calc(90vh-80px)]">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="sticky left-0 bg-gray-50 p-3 text-left text-sm font-semibold text-gray-700 border-b min-w-[120px]">
                      Features
                    </th>
                    {compareList.map(pkg => (
                      <th key={pkg.packageId} className="p-3 text-center border-b min-w-[200px]">
                        <div className="flex flex-col items-center">
                          <div className="p-2 bg-red-50 rounded-lg mb-2">
                            {getPackageIcon(pkg.packageName)}
                          </div>
                          <h3 className="text-sm font-bold text-gray-800 line-clamp-2">
                            {pkg.packageName}
                          </h3>
                          <button
                            onClick={() => removeFromCompare(pkg.packageId)}
                            className="mt-1 text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      💰 Price
                    </td>
                    {compareList.map(pkg => {
                      const priceInfo = calculatePackagePrice(pkg);
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          <span className="text-lg font-bold text-green-600">
                            ₹{priceInfo.finalPrice.toLocaleString()}
                          </span>
                          {priceInfo.hasDiscount && (
                            <span className="text-xs text-gray-400 line-through ml-2">
                              ₹{priceInfo.originalPrice.toLocaleString()}
                            </span>
                          )}
                          {priceInfo.hasDiscount && (
                            <div className="text-[10px] text-red-500 font-semibold">
                              Save ₹{priceInfo.discountAmount.toLocaleString()}
                            </div>
                          )}
                          {!priceInfo.hasDiscount && priceInfo.originalPrice > 0 && priceInfo.originalPrice !== priceInfo.finalPrice && (
                            <div className="text-[10px] text-gray-400">
                              MRP: ₹{priceInfo.originalPrice.toLocaleString()}
                            </div>
                          )}
                          {!priceInfo.hasDiscount && priceInfo.originalPrice === priceInfo.finalPrice && priceInfo.originalPrice > 0 && (
                            <div className="text-[10px] text-gray-400">
                              No Discount
                            </div>
                          )}
                          {priceInfo.originalPrice === 0 && priceInfo.finalPrice === 0 && (
                            <div className="text-[10px] text-gray-400">
                              Price not available
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      🎯 Discount
                    </td>
                    {compareList.map(pkg => {
                      const priceInfo = calculatePackagePrice(pkg);
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          {priceInfo.hasDiscount ? (
                            <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-bold">
                              {priceInfo.discountPercent}% OFF
                            </span>
                          ) : (
                            <span className="text-gray-400">No Discount</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      📋 Total Tests
                    </td>
                    {compareList.map(pkg => {
                      const count = getTestsCount(pkg);
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          <span className="text-lg font-bold text-blue-600">{count}</span>
                          <span className="text-sm text-gray-500"> tests</span>
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      🏥 Lab
                    </td>
                    {compareList.map(pkg => (
                      <td key={pkg.packageId} className="p-3 text-center border-b">
                        <span className="text-sm">{pkg.labName || 'N/A'}</span>
                      </td>
                    ))}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      🏢 Company
                    </td>
                    {compareList.map(pkg => (
                      <td key={pkg.packageId} className="p-3 text-center border-b">
                        <span className="text-sm">{pkg.companyName || 'N/A'}</span>
                      </td>
                    ))}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      ⏰ Fasting Required
                    </td>
                    {compareList.map(pkg => (
                      <td key={pkg.packageId} className="p-3 text-center border-b">
                        {pkg.isFasting ? (
                          <span className="text-orange-500 font-medium">✅ Yes</span>
                        ) : (
                          <span className="text-green-500 font-medium">❌ No</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      ⭐ Popular
                    </td>
                    {compareList.map(pkg => (
                      <td key={pkg.packageId} className="p-3 text-center border-b">
                        {pkg.isPopular ? (
                          <span className="text-yellow-500 font-bold">🌟 Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  <tr>
                    <td colSpan={compareList.length + 1} className="p-4">
                      <h4 className="font-bold text-gray-700 mb-3">📊 Test Details</h4>
                      <div className="space-y-2">
                        {allTestNames.length > 0 ? (
                          allTestNames.map(testName => (
                            <div key={testName} className="grid grid-cols-1 gap-2" style={{ gridTemplateColumns: `120px repeat(${compareList.length}, 1fr)` }}>
                              <div className="text-sm text-gray-600 font-medium p-2 bg-gray-50 rounded">
                                {testName}
                              </div>
                              {compareList.map(pkg => {
                                let hasTest = false;
                                if (pkg.testPackageDetailsJson) {
                                  try {
                                    const tests = JSON.parse(pkg.testPackageDetailsJson);
                                    if (Array.isArray(tests)) {
                                      hasTest = tests.some(test => test.testName === testName);
                                    }
                                  } catch {}
                                }
                                return (
                                  <div key={`${pkg.packageId}-${testName}`} className="p-2 text-center">
                                    {hasTest ? (
                                      <Check size={20} className="text-green-600 mx-auto" />
                                    ) : (
                                      <Minus size={20} className="text-gray-300 mx-auto" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No test details available</p>
                        )}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={compareList.length + 1} className="p-4 bg-gray-50">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => setShowCompareModal(false)}
                          className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                          Close
                        </button>
                        <button
                          onClick={() => {
                            setShowCompareModal(false);
                            if (compareList.length > 0) {
                              setSelectedPackageId(compareList[0].packageId);
                              setShowPackageDetails(true);
                            }
                          }}
                          className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading active packages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 text-sm">{error}</p>
          <button
            onClick={() => fetchAllPackages()}
            className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50/30 py-8 px-4 md:px-8">
      
      {showPackageDetails && (
        <GetPackageDetails
          id={selectedPackageId}
          onClose={() => setShowPackageDetails(false)}
        />
      )}

      <CompareModal />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
            <ShoppingBag size={14} className="text-yellow-400" />
            <span className="text-xs font-bold tracking-wide">ACTIVE PACKAGES</span>
            <Star size={14} className="text-yellow-400" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-extrabold">
            <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
              Our
            </span>{" "}
            <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Health Packages
            </span>
          </h1>
          
          <p className="text-gray-500 text-sm mt-2 max-w-2xl mx-auto">
            Choose from our comprehensive range of active health checkup packages designed for your well-being
          </p>
          
          <div className="mt-2 flex items-center justify-center gap-4 flex-wrap">
            <span className="text-xs text-gray-400">
              {filteredPackages.length} active packages available
            </span>
            {compareList.length > 0 && (
              <button
                onClick={() => setShowCompareModal(true)}
                className="flex items-center gap-2 bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-red-600 transition-colors shadow-md"
              >
                <GitCompare size={14} />
                Compare ({compareList.length})
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search active packages by name, lab or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter size={18} />
              <span>Filters</span>
              {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
            >
              <option value="popular">Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="discount">Biggest Discount</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lab</label>
                <select
                  value={selectedLab}
                  onChange={(e) => setSelectedLab(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="all">All Labs</option>
                  {uniqueLabs.map(lab => (
                    <option key={lab.id} value={lab.id}>{lab.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="all">All Companies</option>
                  {uniqueCompanies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Price: ₹{priceRange.max}</label>
                <input
                  type="range"
                  min="0"
                  max="20000"
                  step="50"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                  className="w-full accent-red-500"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>₹0</span>
                  <span>₹20,000</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredPackages.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">No active packages found</h3>
            <p className="text-gray-500 mt-2">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedLab('all');
                setSelectedCompany('all');
                setPriceRange({ min: 0, max: 20000 });
              }}
              className="mt-4 text-red-500 hover:text-red-600 text-sm font-semibold"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* PACKAGES GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentPackages.map((pkg) => {
            const priceInfo = calculatePackagePrice(pkg);
            const { average: averageRating, total: totalReviews } = getPackageRating(pkg.packageId);
            
            const inCompare = isInCompare(pkg.packageId);
            const inCart = isPackageAdded(pkg.packageId);
            const inWishlist = isInWishlist(pkg.packageId, 3);
            const hasPopular = pkg.isPopular === true;
            const hasFasting = pkg.isFasting === true;
            const hasDiscount = priceInfo.hasDiscount;
            
            return (
              <div
                key={pkg.packageId}
                className="group relative bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                {/* TOP ROW BADGES - Right side */}
                <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                  {hasPopular && (
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                      <Star size={10} fill="white" />
                      POPULAR
                    </div>
                  )}
                  
                  <button
                    onClick={(e) => toggleWishlist(pkg, e)}
                    className="p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                    disabled={wishlistLoading}
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Heart 
                        size={14} 
                        className={`transition-colors duration-200 ${
                          inWishlist 
                            ? 'fill-red-500 text-red-500' 
                            : 'text-gray-400 hover:text-red-500'
                        }`}
                      />
                    )}
                  </button>
                </div>

                {/* TOP ROW BADGES - Left side */}
                <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
                  {hasDiscount && (
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-md">
                      {priceInfo.discountPercent}% OFF
                    </div>
                  )}
                  
                  {hasFasting && (
                    <div className="bg-blue-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                      <Clock size={9} />
                      Fasting
                    </div>
                  )}
                </div>

                {/* ✅ ACTIVE BADGE - Only one at bottom right */}
                <div className="absolute bottom-2 right-2 z-10">
                  <div className="bg-green-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    ACTIVE
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4 pt-12" onClick={() => handlePackageClick(pkg)}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="p-2.5 bg-gradient-to-r from-red-50 to-red-100 rounded-xl group-hover:from-red-100 group-hover:to-red-200 transition-colors">
                      {getPackageIcon(pkg.packageName)}
                    </div>
                  </div>

                  <h3 className="text-sm font-bold text-gray-800 mb-1 line-clamp-1 group-hover:text-red-600 transition-colors">
                    {pkg.packageName}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2 min-h-[32px]">
                    {pkg.description || 'Comprehensive health package'}
                  </p>

                  {/* Rating Stars */}
                  <div className="flex items-center gap-1 mb-2">
                    <div className="flex">
                      {renderStars(averageRating, 11)}
                    </div>
                    {ratingLoading ? (
                      <span className="text-[9px] text-gray-400">Loading...</span>
                    ) : (
                      <span className="text-[9px] text-gray-500">
                        ({totalReviews})
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-[7px] font-bold">✓</span>
                      </div>
                      <span className="text-[9px] text-gray-600 truncate max-w-[80px]">{pkg.labName || 'N/A'}</span>
                    </div>
                    {pkg.companyName && (
                      <>
                        <span className="text-gray-300 text-[9px]">|</span>
                        <span className="text-[9px] text-gray-500 truncate max-w-[60px]">{pkg.companyName}</span>
                      </>
                    )}
                  </div>

                  {/* ✅ PRICE SECTION */}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                    {hasDiscount ? (
                      // ---------- PRICE SECTION (with discount) ----------
                      <>
                        <div>
                          <span className="text-lg font-bold text-green-600">
                            ₹{priceInfo.finalPrice.toLocaleString()}
                          </span>
                          <span className="text-xs line-through text-gray-400 ml-1.5">
                            ₹{priceInfo.originalPrice.toLocaleString()}
                          </span>
                          <span className="block text-[9px] text-red-500 font-semibold">
                            Save ₹{priceInfo.discountAmount.toLocaleString()} ({priceInfo.discountPercent}% OFF)
                          </span>
                        </div>

                        {/* ✅ Bigger Add to Cart Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePackageAction(pkg, e);
                          }}
                          className={`px-4 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-200 ${
                            inCart
                              ? 'bg-red-100 text-red-600 border border-red-400 hover:bg-red-200'
                              : 'bg-blue-100 text-blue-600 border border-blue-400 hover:bg-blue-200'
                          }`}
                        >
                          {inCart ? 'Remove' : 'Add'}
                        </button>
                      </>
                    ) : (
                      // ---------- COUPON SECTION (no discount) ----------
                      <div className="w-full">
                        <div className="flex items-center justify-between w-full">
                          <div>
                            {priceInfo.finalPrice > 0 && (
                              <span className="text-lg font-bold text-green-600">
                                ₹{priceInfo.finalPrice.toLocaleString()}
                              </span>
                            )}
                            {priceInfo.originalPrice > 0 && priceInfo.originalPrice !== priceInfo.finalPrice && (
                              <span className="text-xs line-through text-gray-400 ml-1.5">
                                ₹{priceInfo.originalPrice.toLocaleString()}
                              </span>
                            )}
                            {priceInfo.originalPrice === priceInfo.finalPrice && priceInfo.originalPrice > 0 && (
                              <span className="block text-[9px] text-gray-400 font-medium mt-0.5">
                                No Discount
                              </span>
                            )}
                            {priceInfo.originalPrice === 0 && priceInfo.finalPrice === 0 && (
                              <span className="block text-[9px] text-gray-400 font-medium mt-0.5">
                                Price not available
                              </span>
                            )}
                          </div>

                          {/* ✅ Bigger Add to Cart Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePackageAction(pkg, e);
                            }}
                            className={`px-4 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-200 ${
                              inCart
                                ? 'bg-red-100 text-red-600 border border-red-400 hover:bg-red-200'
                                : 'bg-blue-100 text-blue-600 border border-blue-400 hover:bg-blue-200'
                            }`}
                          >
                            {inCart ? 'Remove' : 'Add'}
                          </button>
                        </div>

                        {/* Coupon Component - Below Price & Add Button */}
                        <div className="mt-1.5">
                          <Coupon
                            labId={pkg.labId || 0}
                            userId={getUserId()}
                            testPrice={priceInfo.originalPrice}
                            sellingPrice={priceInfo.finalPrice}
                            discount={0}
                            showCopyButton={false}
                            showAddToCart={false}
                            isInCart={inCart}
                            onAddToCart={() => handlePackageAction(pkg, { stopPropagation: () => {} })}
                            onRemoveFromCart={() => handlePackageAction(pkg, { stopPropagation: () => {} })}
                            className="w-full"
                            loadingText="Loading coupon..."
                            noCouponText="No coupon available"
                            labelText="🎫 No Discount - Coupon"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COMPARE BUTTON */}
                  <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[9px] text-gray-400 font-medium">
                      🔄 {pkg.testsCount || 0} tests
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCompare(pkg);
                      }}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-all duration-200 ${
                        inCompare 
                          ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                          : 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200'
                      }`}
                    >
                      <GitCompare size={11} />
                      {inCompare ? 'Added' : 'Compare'}
                    </button>
                  </div>
                </div>

                {/* Bottom Border Animation */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent 
                transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </div>
            );
          })}
        </div>

        {/* PAGINATION CONTROLS */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8 mb-4 flex-wrap">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 text-sm
                ${
                  currentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              <ChevronLeft size={14} />
              Previous
            </button>

            <div className="flex gap-1 flex-wrap">
              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' && goToPage(page)}
                  className={`px-3 py-2 rounded-md transition-colors duration-200 min-w-[40px] text-sm
                    ${
                      currentPage === page
                        ? "bg-red-600 text-white"
                        : page === '...'
                        ? "bg-gray-100 text-gray-600 cursor-default"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 text-sm
                ${
                  currentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Showing info */}
        {!loading && filteredPackages.length > 0 && (
          <div className="text-center text-sm text-gray-500 mt-2">
            Showing {startIndex + 1} - {Math.min(endIndex, filteredPackages.length)} of {filteredPackages.length} packages
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Shield size={18} className="text-green-500" />
              <span>{packages.length} Active Packages</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Star size={18} className="text-yellow-500" />
              <span>{packages.filter(p => p.isPopular).length} Popular Packages</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Activity size={18} className="text-blue-500" />
              <span>{uniqueLabs.length} Partner Labs</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Heart size={18} className="text-red-500" />
              <span>{uniqueCompanies.length} Trusted Companies</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Login Popup - Local */}
    <LoginPopup
      isOpen={showLoginPopup}
      onClose={() => setShowLoginPopup(false)}
    />
    
    {/* LoginPopupComponent from useCartActions */}
    {LoginPopupComponent}
    </>
  );
}